import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';

const DRAFT_PREFIX = 'vale-draft';
const DRAFT_TTL_MS = 2 * 24 * 60 * 60 * 1000; // 2 days
const DEBOUNCE_MS = 500;

interface StoredDraft {
  content: string;
  attachments: WorkflowDocument[];
  matterId: number;
  institutionId: number;
  savedAt: number;
}

function getDraftKey(matterId: number, institutionId: number) {
  return `${DRAFT_PREFIX}-${matterId}-${institutionId}`;
}

function loadDraft(
  matterId: number,
  institutionId: number
): { content: string; attachments: WorkflowDocument[] } {
  try {
    const raw = localStorage.getItem(getDraftKey(matterId, institutionId));
    if (!raw) return { content: '', attachments: [] };

    const draft: StoredDraft = JSON.parse(raw);

    if (draft.matterId !== matterId || draft.institutionId !== institutionId) {
      return { content: '', attachments: [] };
    }

    if (Date.now() - draft.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(getDraftKey(matterId, institutionId));
      return { content: '', attachments: [] };
    }

    return {
      content: draft.content,
      attachments: draft.attachments ?? []
    };
  } catch {
    return { content: '', attachments: [] };
  }
}

function saveDraft(
  matterId: number,
  institutionId: number,
  content: string,
  attachments: WorkflowDocument[]
) {
  const key = getDraftKey(matterId, institutionId);

  if (!content.trim() && attachments.length === 0) {
    localStorage.removeItem(key);
    return;
  }

  const draft: StoredDraft = {
    content,
    attachments,
    matterId,
    institutionId,
    savedAt: Date.now()
  };
  localStorage.setItem(key, JSON.stringify(draft));
}

interface UseLocalDraftProps {
  matterId: number;
  institutionId?: number;
}

export function useLocalDraft({ matterId, institutionId }: UseLocalDraftProps) {
  const resolvedInstitutionId = institutionId ?? 0;
  // Bump to force a re-read after clearLocalDraft removes the localStorage entry.
  const [clearVersion, setClearVersion] = useState(0);
  const localDraft = useMemo(
    () => loadDraft(matterId, resolvedInstitutionId),
    // clearVersion is part of the cache key to refresh after a manual clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
    [matterId, resolvedInstitutionId, clearVersion]
  );
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const latestAttachmentsRef = useRef<WorkflowDocument[]>([]);

  const handleDraftChange = useCallback(
    (content: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveDraft(
          matterId,
          resolvedInstitutionId,
          content,
          latestAttachmentsRef.current
        );
      }, DEBOUNCE_MS);
    },
    [matterId, resolvedInstitutionId]
  );

  const saveAttachments = useCallback(
    (attachments: WorkflowDocument[]) => {
      latestAttachmentsRef.current = attachments;
      // Save immediately — attachment changes are discrete user actions
      const key = getDraftKey(matterId, resolvedInstitutionId);
      const raw = localStorage.getItem(key);
      const existing = raw ? (JSON.parse(raw) as StoredDraft) : null;
      const content = existing?.content ?? '';

      saveDraft(matterId, resolvedInstitutionId, content, attachments);
    },
    [matterId, resolvedInstitutionId]
  );

  const clearLocalDraft = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    localStorage.removeItem(getDraftKey(matterId, resolvedInstitutionId));
    setClearVersion((v) => v + 1);
    latestAttachmentsRef.current = [];
  }, [matterId, resolvedInstitutionId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    localDraftContent: localDraft.content,
    localDraftAttachments: localDraft.attachments,
    handleDraftChange,
    saveAttachments,
    clearLocalDraft
  };
}
